import {
  getAuthorization,
  getAuthorizedRequestHeaders,
} from './authorization-util';
import { getSearchValue } from './common-util';

interface CachedBlobUrl {
  objectUrl: string;
  sizeBytes: number;
  lastUsedAt: number;
}

interface CachedArrayBuffer {
  data: ArrayBuffer;
  sizeBytes: number;
  lastUsedAt: number;
}

interface PendingMediaFetch<T> {
  promise: Promise<T>;
  controller: AbortController;
}

const maxCachedItems = 80;
const maxCachedBytes = 160 * 1024 * 1024;
const defaultCacheTtlMs = 30 * 60 * 1000;
const mediaBlobUrlCache = new Map<string, CachedBlobUrl>();
const mediaArrayBufferCache = new Map<string, CachedArrayBuffer>();
const pendingMediaFetches = new Map<string, PendingMediaFetch<string>>();
const pendingArrayBufferFetches = new Map<
  string,
  PendingMediaFetch<ArrayBuffer>
>();
const clearHandlers = new Set<() => void>();

interface MediaCacheOptions {
  authorization?: boolean;
  ignoreQuery?: boolean;
}

export const getMediaCacheKey = (
  url: string,
  options?: { authorization?: boolean; ignoreQuery?: boolean },
) => {
  if (!url) return '';

  const authorizationScope = options?.authorization
    ? `${getAuthorization() || 'anonymous'}:${getSearchValue('shared_id') || ''}`
    : 'public';

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return `${authorizationScope}:${parsedUrl.origin}${parsedUrl.pathname}${
      options?.ignoreQuery ? '' : parsedUrl.search
    }`;
  } catch {
    const [withoutHash] = url.split('#');
    return `${authorizationScope}:${
      options?.ignoreQuery ? withoutHash.split('?')[0] : withoutHash
    }`;
  }
};

const trimMediaCache = () => {
  const trimMap = <T extends { lastUsedAt: number; sizeBytes: number }>(
    cacheMap: Map<string, T>,
    onRemove?: (item: T) => void,
  ) => {
    let totalBytes = [...cacheMap.values()].reduce(
      (sum, item) => sum + item.sizeBytes,
      0,
    );

    if (cacheMap.size <= maxCachedItems && totalBytes <= maxCachedBytes) return;

    const removableItems = [...cacheMap.entries()].sort(
      (a, b) => a[1].lastUsedAt - b[1].lastUsedAt,
    );

    while (
      (cacheMap.size > maxCachedItems || totalBytes > maxCachedBytes) &&
      removableItems.length > 0
    ) {
      const [key, item] = removableItems.shift()!;
      onRemove?.(item);
      cacheMap.delete(key);
      totalBytes = Math.max(0, totalBytes - item.sizeBytes);
    }
  };

  trimMap(mediaBlobUrlCache, (item) => URL.revokeObjectURL(item.objectUrl));
  trimMap(mediaArrayBufferCache);
};

const buildMediaHeaders = (authorization?: boolean) =>
  authorization ? getAuthorizedRequestHeaders() : undefined;

export const getCachedMediaBlobUrl = async (
  url: string,
  options?: MediaCacheOptions,
) => {
  const cacheKey = getMediaCacheKey(url, options);
  if (!cacheKey) return '';

  const cachedItem = mediaBlobUrlCache.get(cacheKey);
  if (cachedItem) {
    if (Date.now() - cachedItem.lastUsedAt > defaultCacheTtlMs) {
      URL.revokeObjectURL(cachedItem.objectUrl);
      mediaBlobUrlCache.delete(cacheKey);
    } else {
      cachedItem.lastUsedAt = Date.now();
      return cachedItem.objectUrl;
    }
  }

  const pendingFetch = pendingMediaFetches.get(cacheKey);
  if (pendingFetch) return pendingFetch.promise;

  const controller = new AbortController();
  const fetchPromise = fetch(url, {
    headers: buildMediaHeaders(options?.authorization),
    signal: controller.signal,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      mediaBlobUrlCache.set(cacheKey, {
        objectUrl,
        sizeBytes: blob.size,
        lastUsedAt: Date.now(),
      });
      trimMediaCache();
      return objectUrl;
    })
    .finally(() => {
      pendingMediaFetches.delete(cacheKey);
    });

  pendingMediaFetches.set(cacheKey, {
    promise: fetchPromise,
    controller,
  });
  return fetchPromise;
};

export const getCachedMediaArrayBuffer = async (
  url: string,
  options?: MediaCacheOptions,
) => {
  const cacheKey = getMediaCacheKey(url, options);
  if (!cacheKey) return new ArrayBuffer(0);

  const cachedItem = mediaArrayBufferCache.get(cacheKey);
  if (cachedItem) {
    if (Date.now() - cachedItem.lastUsedAt > defaultCacheTtlMs) {
      mediaArrayBufferCache.delete(cacheKey);
    } else {
      cachedItem.lastUsedAt = Date.now();
      return cachedItem.data;
    }
  }

  const pendingFetch = pendingArrayBufferFetches.get(cacheKey);
  if (pendingFetch) return pendingFetch.promise;

  const controller = new AbortController();
  const fetchPromise = fetch(url, {
    headers: buildMediaHeaders(options?.authorization),
    signal: controller.signal,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.arrayBuffer();
    })
    .then((data) => {
      mediaArrayBufferCache.set(cacheKey, {
        data,
        sizeBytes: data.byteLength,
        lastUsedAt: Date.now(),
      });
      trimMediaCache();
      return data;
    })
    .finally(() => {
      pendingArrayBufferFetches.delete(cacheKey);
    });

  pendingArrayBufferFetches.set(cacheKey, {
    promise: fetchPromise,
    controller,
  });
  return fetchPromise;
};

export const clearMediaBlobUrlCache = () => {
  pendingMediaFetches.forEach((item) => item.controller.abort());
  pendingArrayBufferFetches.forEach((item) => item.controller.abort());
  mediaBlobUrlCache.forEach((item) => {
    URL.revokeObjectURL(item.objectUrl);
  });
  mediaBlobUrlCache.clear();
  mediaArrayBufferCache.clear();
  pendingMediaFetches.clear();
  pendingArrayBufferFetches.clear();
  clearHandlers.forEach((handler) => {
    try {
      handler();
    } catch (error) {
      console.warn('Failed to clear media cache handler:', error);
    }
  });
};

export const registerMediaCacheClearHandler = (handler: () => void) => {
  clearHandlers.add(handler);
  return () => {
    clearHandlers.delete(handler);
  };
};
